import React, { useState } from "react";
import Papa from "papaparse";
import RecursiveDropdown from "../components/RecursiveDropdown";
import { useAutomateContent } from "../context/AutomateContext";
import DomainInput from "../components/automate/DomainInput";
import { Button, Spinner, Checkbox, FileInput, Label, Accordion } from "flowbite-react";
import { GenerateInternalLinks } from "../components/automate/GenerateInternalLinks";
import { GenerateUpdatedPostContent } from "../components/automate/GenerateUpdatedPostContent";
import { updatePost } from "../components/automate/wordpressApis";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

function SemiAutomateInternalLinkBuilding() {
  const {
    domain,
    setDomain,
    siteCredential,
    setSiteCredential,
    categories,
    selectedCategory,
    setSelectedCategory,
    blogs,
    setBlogs,
    error,
    setError,
    loadingCategories,
    loadingPosts,
  } = useAutomateContent();

  const [selectedPosts, setSelectedPosts] = useState([]);
  const [supportingPosts, setSupportingPosts] = useState([]);
  const [csvData, setCsvData] = useState([]);
  const [processingPillar, setProcessingPillar] = useState();
  const [processingSource, setProcessingSource] = useState();
  const [aiLinkSuggestions, setAiLinkSuggestions] = useState();

  // ✅ Parse uploaded CSV
  const handleCsvUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const parsed = results.data.map((row) => ({
          url: row["URL"]?.trim(),
          primaryKeyword: row["Primary KW"]?.trim() || "",
          secondaryKeywords: row["Secondary KWs"]
            ? row["Secondary KWs"].split(",").map((k) => k.trim())
            : [],
          targetAudience: row["Target Audience"]?.trim() || "",
          l4pk: row["L4PK"]?.trim() || "",
          l4sk: row["L4SK"]?.trim() || "",
        }));
        setCsvData(parsed);
      },
    });
  };

  // ✅ Select/Deselect posts
  const handlePostSelect = (url) => {
    setSelectedPosts((prev) =>
      prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url]
    );
  };

  // ✅ Start automation
  const handleStartSelected = async () => {
    const updatedBlogs = [...blogs];
    let postToUpdate = []

    for (let i = 0; i < updatedBlogs.length; i++) {
      const post = updatedBlogs[i];
      if (!selectedPosts.includes(post.url)) continue;

      setProcessingPillar(post.url);

      // Attach metadata from CSV
      const csvEntry = csvData.find((c) => c.url === post.url);
      const primaryKeyword = csvEntry?.primaryKeyword || "";
      const secondaryKeywords = csvEntry?.secondaryKeywords || [];
      const postKeywords = [primaryKeyword, ...secondaryKeywords].filter(Boolean);

      const otherPosts = blogs.filter((b) => b.url !== post.url);
      setSupportingPosts(otherPosts); // update state (for UI)

      try {
        // Generate link suggestions
        const aiLinkSuggestions = await GenerateInternalLinks({
          targetUrl: post.url,
          keywords: postKeywords,
          supportingArticles: otherPosts || [],
          primaryKeyword,
          secondaryKeywords,
          targetAudience: csvEntry?.targetAudience,
          l4pk: csvEntry?.l4pk,
          l4sk: csvEntry?.l4sk,
        });

        console.log("aiLinkSuggestions", aiLinkSuggestions);
        setAiLinkSuggestions(aiLinkSuggestions);

        // ✅ Pass otherPosts explicitly
        const updatedPosts = await parseAndAssociateLinkSuggestions(
          aiLinkSuggestions,
          otherPosts
        );
        postToUpdate = updatedPosts
        console.log("updatedPosts", updatedPosts);
        // setSelectedPosts(updatedPosts.map((p) => p.url));
      } catch (err) {
        console.error("Error generating internal links for", post.url, err);
      }

      // Update other posts with new content
      for (let j = 0; j < postToUpdate.length; j++) {
        const otherPost = postToUpdate[j];
        if (otherPost.url === post.url) continue;

        setProcessingSource(otherPost.url);

        try {
          const updatedPost = await GenerateUpdatedPostContent({ 
            post: otherPost,
            targetUrl: processingPillar,
          });
          // postToUpdate[j] = updatedPost;

          console.log(updatedPost)

          // Save to WordPress
          await updatePost(siteCredential, otherPost.id, { content: updatedPost });

          setProcessingSource("");
          // setBlogs([...postToUpdate]);
        } catch (err) {
          console.error("Error updating post content for", otherPost.url, err);
        }
      }
    }

    setProcessingPillar("");
    alert("Selected posts processed successfully!");
  };

  // ✅ Fix: pass supportingPosts as arg
  const parseAndAssociateLinkSuggestions = async (responseText, supportingPostsArg) => {
    console.log("Before update:", supportingPostsArg);

    const tableRegex = /\| Priority \|[\s\S]*?(?=(\n\n|\n?$))/g;
    const tables = responseText.match(tableRegex) || [];

    let linkSuggestionsMap = {};

    tables.forEach((table) => {
      const lines = table.split("\n").filter((line) => line.trim().startsWith("|"));
      const dataLines = lines.slice(2); // skip header + separator

      dataLines.forEach((line) => {
        const cols = line
          .split("|")
          .map((col) => col.trim())
          .filter((col) => col.length > 0);

        if (cols.length >= 5) {
          const priority = cols[0];
          const sourceArticle = cols[1]; // must match post.url
          const anchorText = cols[2];
          const exactPlacement = cols[3];
          const updatedParagraph = cols[4];

          if (!linkSuggestionsMap[sourceArticle]) {
            linkSuggestionsMap[sourceArticle] = [];
          }
          linkSuggestionsMap[sourceArticle].push({
            priority,
            anchorText,
            exactPlacement,
            updatedParagraph,
          });
        }
      });
    });

    // Merge suggestions into supporting posts
    const updatedPosts = supportingPostsArg.map((post) => {
      const suggestions = linkSuggestionsMap[post.url] || [];
      return { ...post, linkSuggestions: suggestions };
    });

    console.log("After update:", updatedPosts);
    return updatedPosts;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <h1 className="text-3xl font-bold mb-6 text-center">
        Automate Internal Linking
      </h1>

      <DomainInput
        domain={domain}
        setDomain={setDomain}
        siteCredential={siteCredential}
        setSiteCredential={setSiteCredential}
        setError={setError}
      />

      {error && <p className="text-red-600 mt-2">{error}</p>}
      {loadingCategories && <p className="text-gray-600 mt-2">Loading categories...</p>}
      {categories.length > 0 && (
        <RecursiveDropdown
          categories={categories}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
        />
      )}
      {loadingPosts && <p className="text-gray-600 mt-2">Loading posts...</p>}

      {blogs.length > 0 && (
        <div>
          <div className="mb-4 flex justify-between items-center gap-4">
            <div className="flex flex-col">
              <Label className="mb-2 block" htmlFor="file-upload">
                Upload keywords file (url, primary, secondary, audience)
              </Label>
              <FileInput id="file-upload" accept=".csv" onChange={handleCsvUpload} />
            </div>
            <Button
              onClick={handleStartSelected}
              disabled={processingPillar || selectedPosts.length === 0}
            >
              {processingPillar
                ? "Processing..."
                : `Start Selected (${selectedPosts.length})`}
            </Button>
          </div>

          <div className="flex gap-6">
            {/* Left: All Blogs */}
            <div className="w-2/3 border p-4 rounded">
              <div className="flex flex-col gap-2">
                <div className="mb-2 ml-2 flex items-center gap-2">
                  <Checkbox
                    checked={selectedPosts.length === blogs.length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedPosts(blogs.map((post) => post.url));
                      } else {
                        setSelectedPosts([]);
                      }
                    }}
                    className="bg-white shadow"
                  />
                  <span className="font-medium">Select All</span>
                </div>
                {blogs.map((post) => (
                  <div key={post.url} className="border rounded p-2 hover:shadow-md">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={selectedPosts.includes(post.url)}
                          onChange={() => handlePostSelect(post.url)}
                          className="bg-white shadow"
                        />
                        <div>
                          <p className="font-medium">{post.title}</p>
                          <p className="text-sm text-gray-600">{post.url}</p>
                          <p className="text-xs text-gray-500">
                            Word count: {post.wordCount}
                          </p>
                        </div>
                      </div>
                      {post.url === processingPillar && (
                        <div className="flex items-center gap-2">
                          <Spinner size="sm" />
                          <span>Processing...</span>
                        </div>
                      )}
                    </div>
                    {post.url === processingPillar && (
                      <Accordion>
                        <Accordion.Panel>
                          <Accordion.Title>Link suggestions</Accordion.Title>
                          <Accordion.Content>
                            <div className="mb-4 p-4 border rounded prose max-w-full whitespace-pre-wrap">
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {aiLinkSuggestions}
                              </ReactMarkdown>
                            </div>
                          </Accordion.Content>
                        </Accordion.Panel>
                      </Accordion>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Supporting Posts */}
            <div className="w-1/3 border p-4 rounded">
              {processingPillar && (
                <div className="mb-6 flex gap-4">
                  <div className="border rounded p-4 bg-white">
                    <h2 className="font-bold mb-2">Other Posts Used for Update</h2>
                    <div className="flex flex-col gap-2 max-h-[400px] overflow-y-auto">
                      {supportingPosts.map((post) => (
                        <div
                          key={post.url}
                          className="flex items-center justify-between border rounded p-2 hover:shadow-md"
                        >
                          <div>
                            <p className="font-medium">{post.title}</p>
                            <p className="text-sm text-gray-600">{post.url}</p>
                            <p className="text-xs text-gray-500">
                              Word count: {post.wordCount}
                            </p>
                          </div>
                          {post.url === processingSource && (
                            <div className="flex items-center gap-2">
                              <Spinner size="sm" />
                              <span>Updating content...</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SemiAutomateInternalLinkBuilding;
